import { useMemo, useState } from "react";
import { Ban, ChevronLeft, ChevronRight, CircleX, Copy, Eye, Link2, RefreshCw, RotateCcw, Search } from "lucide-react";
import { api } from "../lib/api";
import { displayError, formatDate, formatDuration, formatNumber, truncate } from "../lib/format";
import { notify } from "../lib/notify";
import type { Task } from "../lib/types";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorState, LoadingState, Modal, PageHeader, StatusBadge } from "../components/Ui";

const statuses = ["", "QUEUED", "PREPARING", "WAITING_SESSION", "SUBMITTING", "RUNNING", "FINALIZING", "RETRY_WAIT", "UNKNOWN_REMOTE", "CANCEL_REQUESTED", "SUCCEEDED", "FAILED", "TIMED_OUT", "CANCELLED"];
const terminalStatuses = new Set(["SUCCEEDED", "FAILED", "TIMED_OUT", "CANCELLED"]);
const retryableStatuses = new Set(["FAILED", "TIMED_OUT", "CANCELLED"]);
const reconciliationStatuses = new Set(["UNKNOWN_REMOTE", "CANCEL_REQUESTED"]);
const reconciliationStages = new Set(["unknown_remote", "polling_unknown", "deadline_unknown"]);

export default function TasksPage() {
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Task | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const limit = 50;
  const { data, loading, error, refresh } = useResource(
    (signal) => api.tasks({ limit, offset: page * limit, status: status || undefined, q: search || undefined }, signal),
    [page, status, search],
    8_000,
  );
  const tasks = data?.items || [];
  const pages = Math.max(1, Math.ceil((data?.total || 0) / limit));

  const visibleRange = useMemo(() => {
    if (!data?.total) return "0";
    return `${page * limit + 1}-${Math.min((page + 1) * limit, data.total)} / ${formatNumber(data.total)}`;
  }, [data?.total, page]);

  async function copy(value: string) {
    await navigator.clipboard.writeText(value);
    notify("任务 ID 已复制", "success");
  }

  async function openResult(task: Task) {
    if (busy) return;
    // Reserve the new tab synchronously so popup blockers do not discard the
    // result while the authenticated request for a fresh signed URL is pending.
    const popup = window.open("about:blank", "_blank");
    if (popup) popup.opener = null;
    setBusy(task.id);
    try {
      const latest = await api.task(task.id);
      const url = latest.video_url || latest.result_url;
      if (!url) throw new Error("视频结果暂不可用");
      if (popup && !popup.closed) {
        popup.location.replace(url);
        popup.focus();
      } else {
        window.location.assign(url);
      }
    } catch (caught) {
      popup?.close();
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  async function cancel(task: Task) {
    if (!window.confirm(`确认取消任务 ${task.id}？已提交到上游的任务仍会轮询到终态。`)) return;
    setBusy(task.id);
    try {
      await api.cancelTask(task.id);
      notify("取消请求已提交", "success");
      await refresh();
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  async function retry(task: Task) {
    if (!window.confirm(`确认重新排队任务 ${task.id}？`)) return;
    setBusy(task.id);
    try {
      await api.retryTask(task.id);
      notify("任务已重新排队", "success");
      await refresh();
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  async function reconcile(task: Task) {
    const remoteJobSetId = task.remote_job_set_id || window.prompt("输入已核对的 Higgsfield Job Set ID")?.trim();
    if (!remoteJobSetId) return;
    const remoteJobId = task.remote_job_id || (task.remote_job_set_id ? undefined : window.prompt("输入 Job ID（可留空）")?.trim());
    setBusy(task.id);
    try {
      await api.reconcileTask(task.id, remoteJobSetId, remoteJobId || undefined);
      notify("已绑定远端任务，将继续轮询", "success");
      setSelected(null);
      await refresh();
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  async function confirmNotCreated(task: Task) {
    if (!window.confirm("仅在已人工确认 Higgsfield 没有创建该任务时继续。确认后将释放账号槽位。")) return;
    setBusy(task.id);
    try {
      await api.confirmTaskNotCreated(task.id);
      notify("任务已结束，账号槽位已释放", "success");
      setSelected(null);
      await refresh();
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="视频任务"
        description="持久化队列中的创建、轮询与终态任务"
        actions={<button className="button button-secondary" onClick={() => void refresh()}><RefreshCw size={16} />刷新</button>}
      />

      <div className="filter-bar">
        <form className="search-box" onSubmit={(event) => { event.preventDefault(); setPage(0); setSearch(query.trim()); }}>
          <Search size={16} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="任务 ID、提示词或远端 ID" aria-label="搜索任务" />
        </form>
        <select value={status} onChange={(event) => { setPage(0); setStatus(event.target.value); }} aria-label="按状态筛选">
          {statuses.map((item) => <option key={item || "all"} value={item}>{item ? item.replaceAll("_", " ") : "全部状态"}</option>)}
        </select>
        {(status || search) && <button className="button button-quiet" onClick={() => { setStatus(""); setSearch(""); setQuery(""); setPage(0); }}>清除筛选</button>}
      </div>

      <section className="panel panel-flush">
        {loading && !data ? <LoadingState /> : error && !data ? <ErrorState error={error} onRetry={refresh} /> : !tasks.length ? <EmptyState label="没有匹配的任务" /> : (
          <div className="table-wrap">
            <table className="tasks-table">
              <thead><tr><th>任务</th><th>状态</th><th>进度</th><th>模型 / 账号</th><th>创建时间</th><th>错误 / 结果</th><th className="actions-cell">操作</th></tr></thead>
              <tbody>{tasks.map((task) => {
                const normalizedStatus = String(task.status).toUpperCase();
                const progress = Math.min(100, Math.max(0, Number(task.progress || (normalizedStatus === "SUCCEEDED" ? 100 : 0))));
                const operatorError = task.upstream_diagnostics?.reason || (task.error ? displayError(task.error) : "");
                return (
                  <tr key={task.id}>
                    <td><div className="primary-cell"><code title={task.id}>{truncate(task.id, 22)}</code><span title={task.prompt}>{truncate(task.prompt || task.parameters?.prompt || "", 46)}</span></div></td>
                    <td><StatusBadge value={task.status} />{task.diagnostic_reason && !terminalStatuses.has(normalizedStatus) ? <span className="cell-note" title={task.diagnostic_reason}>{truncate(task.diagnostic_reason, 28)}</span> : task.attempt_count ? <span className="cell-note">尝试 {task.attempt_count}</span> : null}</td>
                    <td><div className="table-progress"><span><i style={{ width: `${progress}%` }} /></span><small>{progress}%</small></div></td>
                    <td><div className="primary-cell"><strong>{task.model || "videos-mini"}</strong><span>账号 {task.account_id || "待分配"}</span></div></td>
                    <td><div className="primary-cell"><span>{formatDate(task.created_at)}</span><small>更新 {formatDate(task.updated_at)}</small></div></td>
                    <td className={operatorError ? "cell-error" : ""} title={operatorError}>{operatorError ? truncate(operatorError, 54) : task.video_url || task.result_url ? <a href={task.video_url || task.result_url || "#"} onClick={(event) => { event.preventDefault(); void openResult(task); }}>打开结果</a> : <span className="muted">-</span>}</td>
                    <td className="actions-cell">
                      <button className="icon-button" title="查看详情" aria-label="查看详情" onClick={() => setSelected(task)}><Eye size={17} /></button>
                      <button className="icon-button" title="复制任务 ID" aria-label="复制任务 ID" onClick={() => void copy(task.id)}><Copy size={17} /></button>
                      {!terminalStatuses.has(normalizedStatus) && normalizedStatus !== "CANCEL_REQUESTED" && <button className="icon-button icon-button-danger" title="取消任务" aria-label="取消任务" disabled={busy === task.id} onClick={() => void cancel(task)}><Ban size={17} /></button>}
                      {retryableStatuses.has(normalizedStatus) && <button className="icon-button icon-button-success" title="重新排队" aria-label="重新排队" disabled={busy === task.id} onClick={() => void retry(task)}><RotateCcw size={17} /></button>}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
        <footer className="table-footer">
          <span>{visibleRange}</span>
          <div>
            <button className="icon-button" disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))} aria-label="上一页"><ChevronLeft size={17} /></button>
            <span>第 {page + 1} / {pages} 页</span>
            <button className="icon-button" disabled={page + 1 >= pages} onClick={() => setPage((value) => value + 1)} aria-label="下一页"><ChevronRight size={17} /></button>
          </div>
        </footer>
      </section>

      <Modal open={Boolean(selected)} onClose={() => setSelected(null)} title="任务详情" width="large">
        {selected && <div className="detail-stack">
          <div className="detail-grid">
            <Detail label="任务 ID" value={selected.id} code />
            <Detail label="状态" value={<StatusBadge value={selected.status} />} />
            <Detail label="账号" value={selected.account_id || "待分配"} />
            <Detail label="模型" value={selected.model || "videos-mini"} />
            <Detail label="远端 Job Set" value={selected.remote_job_set_id || "-"} code />
            <Detail label="远端 Job" value={selected.remote_job_id || "-"} code />
            <Detail label="创建" value={formatDate(selected.created_at)} />
            <Detail label="完成" value={formatDate(selected.completed_at)} />
            <Detail label="当前阶段" value={selected.diagnostic_stage || selected.create_stage || selected.status} />
            <Detail label="阶段说明" value={selected.diagnostic_reason || "-"} />
            <Detail label="最近更新" value={formatDuration(selected.last_update_age_seconds)} />
            <Detail label="下次动作" value={selected.next_action_at ? formatDate(selected.next_action_at) : "-"} />
            <Detail label="截止剩余" value={formatRemaining(selected.deadline_remaining_seconds)} />
            <Detail label="轮询状态" value={selected.last_poll_reason || "-"} />
            <Detail label="连续轮询错误" value={selected.consecutive_poll_errors || 0} />
          </div>
          {needsRemoteReconciliation(selected) && <div className="callout callout-warning"><span>{reconciliationMessage(selected)}</span><div className="modal-actions"><button className="button button-secondary" disabled={busy === selected.id} onClick={() => void reconcile(selected)}><Link2 size={16} />{selected.remote_job_set_id ? "恢复轮询" : "绑定远端任务"}</button>{!selected.remote_job_set_id && selected.create_stage === "unknown_remote" && <button className="button button-danger" disabled={busy === selected.id} onClick={() => void confirmNotCreated(selected)}><CircleX size={16} />确认未创建</button>}</div></div>}
          {selected.upstream_diagnostics && <div>
            <h3 className="detail-heading">上游真实原因</h3>
            <div className="detail-grid">
              <Detail label="来源" value={selected.upstream_diagnostics.source === "local" ? "本地处理" : "上游服务"} />
              <Detail label="判定" value={selected.upstream_diagnostics.reason || "-"} />
              <Detail label="原始错误码" value={selected.upstream_diagnostics.code || "-"} code />
              <Detail label="HTTP 状态" value={selected.upstream_diagnostics.http_status ?? "-"} />
              <Detail label="发生阶段" value={selected.upstream_diagnostics.phase || selected.create_stage || "-"} />
              <Detail label="上游端点" value={selected.upstream_diagnostics.endpoint || "-"} code />
            </div>
            {selected.upstream_diagnostics.message && <pre className="code-block code-error">{selected.upstream_diagnostics.message}</pre>}
            {selected.upstream_diagnostics.response_summary && <><h3 className="detail-heading">上游响应摘要（已脱敏）</h3><pre className="code-block">{formatDiagnosticSummary(selected.upstream_diagnostics.response_summary)}</pre></>}
          </div>}
          {selected.error && <div><h3 className="detail-heading">错误</h3><pre className="code-block code-error">{displayError(selected.error)}</pre></div>}
          <div><h3 className="detail-heading">请求参数</h3><pre className="code-block">{JSON.stringify(selected.parameters || {}, null, 2)}</pre></div>
          {(selected.video_url || selected.result_url) && <a className="button button-primary align-self-start" href={selected.video_url || selected.result_url || "#"} onClick={(event) => { event.preventDefault(); void openResult(selected); }}>打开视频结果</a>}
        </div>}
      </Modal>
    </div>
  );
}

function Detail({ label, value, code = false }: { label: string; value: React.ReactNode; code?: boolean }) {
  return <div className="detail-item"><span>{label}</span>{code ? <code>{value}</code> : <strong>{value}</strong>}</div>;
}

function formatDiagnosticSummary(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function formatRemaining(value: unknown): string {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return "-";
  if (seconds <= 0) return `已超时 ${formatDuration(Math.abs(seconds))}`;
  return formatDuration(seconds);
}

function needsRemoteReconciliation(task: Task): boolean {
  return reconciliationStatuses.has(String(task.status).toUpperCase())
    && reconciliationStages.has(String(task.create_stage || "").toLowerCase());
}

function reconciliationMessage(task: Task): string {
  const cancellationPending = String(task.status).toUpperCase() === "CANCEL_REQUESTED";
  if (task.remote_job_set_id) {
    return cancellationPending
      ? "取消已记录，但远端任务仍需轮询到终态。账号槽位会保留，恢复轮询后继续核对。"
      : "远端任务 ID 已知，但终态尚未确认。账号槽位会保留，恢复轮询后继续核对。";
  }
  return cancellationPending
    ? "取消已记录，但创建结果仍未知。为避免重复扣费，需要先在 Higgsfield 最近任务中核对。"
    : "创建响应未知。为避免重复扣费，该任务不会自动重试，需要先在 Higgsfield 最近任务中核对。";
}
